import { Box, Button, IconButton, Table, TableBody, TableContainer, TablePagination, TextField, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getTablePaginationCount } from 'src/utils';
import Iconify from '../Iconify';
import Scrollbar from '../Scrollbar';
import { ConfirmDeleteDialog } from '../confirm-dialog';
import SkeletonTable from '../skeleton/SkeletonTable';
import { TableHeadCustom, TableNoData, TableSelectedActions } from '../table';

const GeneralTable = (props) => {
  const {
    className,
    tableContainerSX,
    tableData,
    rowID,
    headLabel,
    isLoading,
    isDeleteLoading,
    CustomTableRow,
    onEditRow,
    onGenerateGift,
    onShowGifts,
    onDeleteRow,
    onDeleteRows,
    openConfirmDelete,
    setOpenConfirmDelete,
    useTableOptions,
    selectedRow,
    setSelectedRow,
    hasDeleteOption = true,
    label,
    exportToExcel,
    showGoToPageInput = false,
    ...rest
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { page, setPage, onChangePage, order, orderBy, selected, rowsPerPage, onSelectAllRows, onSelectRow, onRowsPerPageChange } =
    useTableOptions;

  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 0;

  const [inputPage, setInputPage] = useState(currentPage);
  const [maxPage, setMaxPage] = useState();
  const totalTableRows = page * rowsPerPage + tableData?.length;

  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const isNotFound = tableData.length === 0;

  const returnItemName = () => {
    const itemNaming = isMultiSelectMode ? 'آیتم ها' : 'آیتم';
    if (label) {
      return isMultiSelectMode ? label?.[1] ?? itemNaming : label?.[0] ?? itemNaming;
    } else {
      return itemNaming;
    }
  };

  const handleRedirect = () => {
    const newPage = Number(inputPage);
    if (totalTableRows % rowsPerPage === 0 || inputPage <= maxPage) {
      setPage(newPage);
      if (newPage === 0) navigate(location?.pathname);
      else navigate(`${location?.pathname}?page=${newPage}`);
    } else {
      enqueueSnackbar('شماره صفحه موردنظر وجود ندارد', { variant: 'warning' });
    }
  };

  useEffect(() => {
    setInputPage(currentPage);
    if (totalTableRows % rowsPerPage !== 0) {
      setMaxPage(currentPage);
    } else {
      setMaxPage(Infinity);
    }
  }, [page, rowsPerPage, tableData]);

  return (
    <>
      <Scrollbar>
        <TableContainer sx={tableContainerSX}>
          {hasDeleteOption && selected.length > 0 && (
            <TableSelectedActions
              numSelected={selected.length}
              rowCount={tableData.length}
              onSelectAllRows={(checked) =>
                onSelectAllRows(
                  checked,
                  tableData?.map((row) => row[rowID])
                )
              }
              actions={
                <>
                  {exportToExcel ? (
                    <Box sx={{ display: 'flex', gap: '3px' }}>
                      <Tooltip title=" دانلود فایل اکسل">
                        <IconButton color="inherit" sx={{ color: 'black' }} onClick={exportToExcel}>
                          <Iconify icon={'ri:file-excel-2-line'} width={24} height={24} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="حذف">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setOpenConfirmDelete(true);
                            setIsMultiSelectMode(true);
                          }}
                        >
                          <Iconify icon={'eva:trash-2-outline'} width={24} height={24} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Button
                      color="error"
                      variant="contained"
                      onClick={() => {
                        setOpenConfirmDelete(true);
                        setIsMultiSelectMode(true);
                      }}
                    >
                      حذف
                    </Button>
                  )}
                </>
              }
            />
          )}

          <Table stickyHeader size="medium" className={className}>
            <TableHeadCustom
              order={order}
              orderBy={orderBy}
              headLabel={headLabel}
              rowCount={tableData.length}
              {...(hasDeleteOption > 0 && {
                numSelected: selected.length,
                onSelectAllRows: (checked) =>
                  onSelectAllRows(
                    checked,
                    tableData?.map((row) => row[rowID])
                  ),
              })}
            />
            <TableBody>
              {isLoading
                ? [...Array(7)]?.map((_, index) => (
                    <SkeletonTable key={index} numOfColumns={hasDeleteOption ? headLabel.length + 1 : headLabel.length} />
                  ))
                : tableData?.map((row, index) => (
                    <CustomTableRow
                      row={row}
                      key={row[rowID]}
                      index={index}
                      rowNumber={page * rowsPerPage + index + 1}
                      selected={selected.includes(row[rowID])}
                      setSelectedRow={setSelectedRow}
                      items={tableData}
                      onEditRow={() => onEditRow(row)}
                      onGenerateGift={() => onGenerateGift(row[rowID])}
                      onShowGifts={() => onShowGifts(row[rowID])}
                      onSelectRow={() => onSelectRow(row[rowID])}
                      onDeleteRow={() => {
                        setOpenConfirmDelete(true);
                        setSelectedRow(row);
                      }}
                      {...rest}
                    />
                  ))}
              <TableNoData isNotFound={!isLoading && isNotFound} />
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
      {!isLoading && (tableData?.length > 0 || currentPage > 0) ? (
        <div
          className="flex flex-col md:flex-row items-start md:items-center justify-between md:gap-4"
          style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)', paddingTop: '10px' }}
        >
          {showGoToPageInput ? (
            <form onSubmit={handleRedirect}>
              <div className="flex items-center gap-2 mr-5 my-4">
                <TextField
                  label="شماره صفحه"
                  type="number"
                  value={inputPage}
                  onChange={(e) => setInputPage(e.target.value)}
                  size="small"
                  sx={{ width: '100px' }}
                  inputProps={{ style: { textAlign: 'center' }, min: 0 }}
                />
                <Button type="submit" variant="contained" disabled={Number(inputPage) > maxPage || inputPage === ''}>
                  رفتن به صفحه
                </Button>
              </div>
            </form>
          ) : null}

          <TablePagination
            rowsPerPageOptions={[rowsPerPage]}
            component="div"
            count={getTablePaginationCount(page, rowsPerPage, tableData?.length)}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onChangePage}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{ width: '100%', borderTop: 'none' }}
          />
        </div>
      ) : null}
      <ConfirmDeleteDialog
        open={openConfirmDelete}
        onClose={() => {
          setOpenConfirmDelete(false);
          setIsMultiSelectMode(false);
        }}
        confirmDelete={() => {
          if (selected?.length && isMultiSelectMode) {
            onDeleteRows(selected);
          } else {
            onDeleteRow(selectedRow[rowID]);
          }
        }}
        title={`آیا برای حذف 
          ${returnItemName()}
            اطمینان دارید؟`}
        isLoading={isDeleteLoading}
      />
    </>
  );
};

export default GeneralTable;
